const ACTIVE_ORDER_STATUSES = new Set(['pending', 'preparing']);
const UNPAID_ORDER_STATUSES = new Set(['pending', 'preparing', 'completed']);

const SERVICE_STATE_ALIASES = {
  active: 'active',
  occupied: 'active',
  busy: 'active',
  full: 'active',
  dolu: 'active',
  reserved: 'reserved',
  rezerve: 'reserved',
  inactive: 'inactive',
  disabled: 'inactive',
  pasif: 'inactive',
  cleaning: 'inactive',
  clean: 'inactive',
  temizlik: 'inactive',
  temizlikte: 'inactive',
};

const STATUS_LABELS = {
  available: 'Müsait',
  occupied: 'Dolu',
  attention: 'Talep Var',
  reserved: 'Rezerve',
  inactive: 'Servis Dışı',
};

const getOrderTableId = (order) => order?.table_id ?? order?.tableId ?? null;
const getRequestTableId = (request) =>
  request?.table?.id ?? request?.table_id ?? request?.tableId ?? null;

const getPendingRequestCountForTable = (requests = [], tableId) =>
  requests.filter((request) => request?.status === 'pending' && getRequestTableId(request) === tableId).length;

const getComputedStatusLabel = (computedStatus) => STATUS_LABELS[computedStatus] || STATUS_LABELS.available;

export const normalizeTableServiceState = (value) => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return SERVICE_STATE_ALIASES[normalized] || 'active';
};

export const decorateTablesWithOperationalState = (tables = [], allOrders = []) =>
  tables.map((table) => {
    const tableId = table?.id ?? null;
    const serviceState = normalizeTableServiceState(table?.serviceState ?? table?.status);
    const ordersOfTable = allOrders.filter((order) => getOrderTableId(order) === tableId);
    const activeOrderCount = ordersOfTable.filter(
      (order) => order?.payment_status !== 'paid' && ACTIVE_ORDER_STATUSES.has(order?.status)
    ).length;
    const unpaidOrderCount = ordersOfTable.filter(
      (order) => order?.payment_status !== 'paid' && UNPAID_ORDER_STATUSES.has(order?.status)
    ).length;
    const occupancyState = unpaidOrderCount > 0 ? 'occupied' : 'available';

    let computedStatus = occupancyState;
    if (serviceState !== 'active') {
      computedStatus = serviceState;
    }

    return {
      ...table,
      serviceState,
      occupancyState,
      computedStatus,
      statusLabel: getComputedStatusLabel(computedStatus),
      interactionMode: serviceState === 'active' ? 'full' : 'view_only',
      activeOrderCount,
      unpaidOrderCount,
      pendingRequestCount: 0,
    };
  });

export const decorateTablesWithRequestAttention = (tables = [], requests = []) =>
  tables.map((table) => {
    const serviceState = normalizeTableServiceState(table?.serviceState ?? table?.status);
    const pendingRequestCount = getPendingRequestCountForTable(requests, table?.id);

    let computedStatus = table?.computedStatus || 'available';
    if (serviceState === 'active' && table?.occupancyState !== 'occupied' && pendingRequestCount > 0) {
      computedStatus = 'attention';
    } else if (serviceState === 'active' && table?.occupancyState !== 'occupied') {
      computedStatus = 'available';
    } else if (serviceState !== 'active') {
      computedStatus = serviceState;
    }

    return {
      ...table,
      serviceState,
      computedStatus,
      pendingRequestCount,
      statusLabel: getComputedStatusLabel(computedStatus),
      interactionMode: serviceState === 'active' ? 'full' : 'view_only',
    };
  });

export const getTableSummaryCounts = (tables = []) =>
  tables.reduce(
    (summary, table) => {
      if (table.computedStatus === 'occupied') {
        summary.occupiedCount += 1;
      } else if (table.computedStatus === 'attention') {
        summary.attentionCount += 1;
      } else if (table.computedStatus === 'available') {
        summary.availableCount += 1;
      } else {
        summary.nonServiceableCount += 1;
      }

      return summary;
    },
    {
      occupiedCount: 0,
      attentionCount: 0,
      availableCount: 0,
      nonServiceableCount: 0,
    }
  );
